import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { debounce, throttle } from "lodash-es";

import { apiClient } from "@/core/api/ApiClient";
import { CommunicationCountersDto } from "@/core/api/generated/v1";

import { AppDispatch, AppThunkAction } from "..";

interface CommunicationState {
  loading: {
    getCounters?: boolean;
  };
  counters?: CommunicationCountersDto;
}

const initialState: CommunicationState = {
  loading: {},
  counters: undefined,
};

const slice = createSlice({
  name: "communication",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<CommunicationState["loading"]>) => {
      state.loading = {
        ...state.loading,
        ...action.payload,
      };
    },
    _countersFetched: (state, action: PayloadAction<CommunicationCountersDto>) => {
      state.counters = action.payload;
    },
  },
});

export const { setLoading, _countersFetched } = slice.actions;

export const communicationReducer = slice.reducer;

const _getCounters = async (dispatch: AppDispatch): Promise<CommunicationCountersDto> => {
  dispatch(
    setLoading({
      getCounters: true,
    }),
  );
  try {
    const response = await apiClient.communicationApi.apiV1CommunicationCountersGet({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    });
    await dispatch(_countersFetched(response.data));
    return response.data;
  } finally {
    dispatch(
      setLoading({
        getCounters: false,
      }),
    );
  }
};
const _getCountersDebounce = debounce(_getCounters, 3000, {
  leading: false,
  trailing: true,
});
const _getCountersThrottle = throttle(_getCounters, 3000, {
  leading: true,
  trailing: false,
});

export const getCounters =
  (): AppThunkAction<Promise<CommunicationCountersDto>> => async (dispatch) => {
    return await _getCounters(dispatch);
  };
export const getCountersDebounce =
  (): AppThunkAction<Promise<CommunicationCountersDto>> => async (dispatch) => {
    return (await _getCountersDebounce(dispatch))!;
  };
export const getCountersThrottle =
  (): AppThunkAction<Promise<CommunicationCountersDto>> => async (dispatch) => {
    return (await _getCountersThrottle(dispatch))!;
  };
